// filter.component.ts
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ButtonGroupSize, ButtonType, ButtonVariant, IconName } from '@yupma/ui-kit';
import { FieldDocument, FieldGroupDocument, FilterGroup, FilterCondition, FilterOperator, FieldType, TextOperator, NumberOperator, DateOperator, CheckboxOperator, SelectionOperator, UserDocument, ActivityField } from 'server-model';
import { LogicOperator } from 'server-model/model/filter/filter';

@Component({
  selector: 'app-filter',
  templateUrl: './filter.component.html',
  styleUrl: './filter.component.scss'
})
export class FilterComponent implements OnInit {
  @Input() fieldDocuments: FieldDocument[] | ActivityField[] = [];
  @Input() fieldGroups: FieldGroupDocument[] = [];
  @Input() filter: FilterGroup | null = null;
  @Input() allUsers: UserDocument[] = [];
  @Output() onFilterChange = new EventEmitter<FilterGroup>();

  // UI constants
  buttonGroupSize = ButtonGroupSize;
  buttonTypes = ButtonType;
  buttonVariants = ButtonVariant;
  iconNames = IconName;
  logicOperators = LogicOperator;

  // Filter state
  filterConditions: FilterCondition[] = [];
  selectedLogicOperator: LogicOperator = LogicOperator.AND;
  private invalidConditions = new Set<number>();

  ngOnInit() {
    this.initializeFilter();
  }

  private initializeFilter(): void {
    if (this.filter?.conditions?.length) {
      this.filterConditions = [...this.filter.conditions];
      this.selectedLogicOperator = this.filter.logic || LogicOperator.AND;
    } else {
      this.addCondition();
    }
  }

  addCondition(): void {
    const newCondition = this.createEmptyCondition();
    this.filterConditions.push(newCondition);
    this.emitFilters();
  }

  removeCondition(index: number): void {
    if (this.isValidIndex(index)) {
      this.filterConditions.splice(index, 1);
      this.rebuildInvalidConditions();

      // Ensure at least one condition exists
      if (this.filterConditions.length === 0) {
        this.addCondition();
      } else {
        this.emitFilters();
      }
    }
  }

  onConditionUpdated(index: number, condition: FilterCondition | null): void {
    if (!this.isValidIndex(index)) return;

    if (condition) {
      // Create a new FilterCondition instance to preserve all methods
      this.filterConditions[index] = new FilterCondition(
        condition.field_name,
        condition.operator,
        condition.value,
        condition.extra_value,
        condition.field_type
      );
      this.invalidConditions.delete(index);
    } else {
      // Mark as invalid but don't remove the condition
      this.invalidConditions.add(index);
    }

    this.emitFilters();
  }

  onConditionInvalidationChanged(index: number, isInvalid: boolean): void {
    if (isInvalid) {
      this.invalidConditions.add(index);
    } else {
      this.invalidConditions.delete(index);
    }
    this.emitFilters();
  }

  onLogicOperatorChanged(operator: LogicOperator): void {
    this.selectedLogicOperator = operator;
    this.emitFilters();
  }

  private createEmptyCondition(): FilterCondition {
    const firstField = this.fieldDocuments[0];
    const fieldType = firstField?.field_type as FieldType || FieldType.SINGLE_LINE_TEXT;

    return new FilterCondition(
      firstField?.name || '',
      this.getDefaultOperator(fieldType),
      '',
      undefined,
      fieldType
    );
  }

  private getDefaultOperator(fieldType: FieldType): FilterOperator {
    const operatorMap: Record<FieldType, FilterOperator> = {
      [FieldType.SINGLE_LINE_TEXT]: TextOperator.EQUALS,
      [FieldType.MULTI_LINE_TEXT]: TextOperator.EQUALS,
      [FieldType.RICH_TEXT]: TextOperator.EQUALS,
      [FieldType.EMAIL]: TextOperator.EQUALS,
      [FieldType.PHONE]: TextOperator.EQUALS,
      [FieldType.NUMBER]: NumberOperator.EQUALS,
      [FieldType.DATE]: DateOperator.EQUALS,
      [FieldType.DATE_AND_TIME]: DateOperator.EQUALS,
      [FieldType.CHECKBOX]: CheckboxOperator.EQUALS,
      [FieldType.DROPDOWN]: SelectionOperator.IS_ANY,
      [FieldType.MULTI_SELECT_DROPDOWN]: SelectionOperator.IS_ANY,
      [FieldType.USER]: SelectionOperator.IS_ANY,
      [FieldType.FILE]: TextOperator.EQUALS,
      [FieldType.JSON]: TextOperator.EQUALS,
      [FieldType.LOCATION]: TextOperator.EQUALS
    };

    return operatorMap[fieldType] || TextOperator.EQUALS;
  }

  private isValidIndex(index: number): boolean {
    return index >= 0 && index < this.filterConditions.length;
  }

  private rebuildInvalidConditions(): void {
    const currentInvalidIndices = Array.from(this.invalidConditions);
    this.invalidConditions.clear();

    currentInvalidIndices.forEach(oldIndex => {
      if (oldIndex < this.filterConditions.length) {
        this.invalidConditions.add(oldIndex);
      }
    });
  }

  private getValidConditions(): FilterCondition[] {
    return this.filterConditions.filter((_, index) =>
      !this.invalidConditions.has(index)
    );
  }

  private emitFilters(): void {
    const validConditions = this.getValidConditions();
    const filterGroup = new FilterGroup(this.selectedLogicOperator, validConditions);
    this.onFilterChange.emit(filterGroup);
  }

  // Public API methods
  getCurrentFilter(): FilterGroup {
    const validConditions = this.getValidConditions();
    return new FilterGroup(this.selectedLogicOperator, validConditions);
  }

  getAllConditionsForStorage(): FilterGroup {
    const storageConditions = this.filterConditions.map(condition => ({
      field_name: condition.field_name,
      operator: condition.operator,
      value: condition.value,
      extra_value: condition.extra_value,
      field_type: condition.field_type
    }));

    return {
      logic: this.selectedLogicOperator,
      conditions: storageConditions
    } as FilterGroup;
  }

  hasValidConditions(): boolean {
    return this.getValidConditions().length > 0;
  }

  getValidationSummary(): { total: number; valid: number; invalid: number } {
    const total = this.filterConditions.length;
    const invalid = this.invalidConditions.size;
    return {
      total,
      valid: total - invalid,
      invalid
    };
  }

  loadSavedFilter(savedFilter: FilterGroup): void {
    this.selectedLogicOperator = savedFilter.logic || LogicOperator.AND;
    this.filterConditions = savedFilter.conditions.map(condition =>
      new FilterCondition(
        condition.field_name,
        condition.operator,
        condition.value,
        condition.extra_value,
        condition.field_type
      )
    );
    this.invalidConditions.clear();

    if (this.filterConditions.length === 0) {
      this.addCondition();
    }
  }

  clearAllFilters(): void {
    this.filterConditions = [];
    this.invalidConditions.clear();
    this.selectedLogicOperator = LogicOperator.AND;
    this.addCondition();
  }

  isConditionValid(index: number): boolean {
    return !this.invalidConditions.has(index);
  }

  trackByConditionIndex(index: number): number {
    return index;
  }
}